import React, { useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { area as d3Area } from "d3-shape";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas, getMouseCanvas } from "../GenericComponent";
import { ichimoku as ichimokuFactory } from "../calculator";
import { hexToRGBA, isDefined, last, parseIntradayPeriodicity } from "../utils";
import { drawOnCanvas as drawEdgeOnCanvas } from "../coordinates/EdgeCoordinateV3";

function parsePeriodicityMs(periodicity) {
  const parsed = parseIntradayPeriodicity(periodicity);
  if (!parsed) return 24 * 60 * 60 * 1000;
  return parsed.unit === "h" ? parsed.value * 3600000 : parsed.value * 60000;
}

const INTRADAY_PERIODS = [
  "1min",
  "2min",
  "3min",
  "4min",
  "5min",
  "10min",
  "15min",
  "20min",
  "30min",
  "60min",
  "120min",
  "240min",
];
function formatFutureDate(date, periodicity) {
  try {
    if (INTRADAY_PERIODS.includes(periodicity)) {
      return date.toLocaleDateString("pt-BR", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return date.toLocaleDateString("pt-BR", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return date.toString();
  }
}

function buildPoints(projection, fullData, xScale, yScale, barWidth) {
  const lastReal = last(fullData);
  if (!lastReal || !lastReal.idx) return [];

  const lastRealIdxValue = lastReal.idx.index;
  const lastRealX = xScale(lastRealIdxValue);

  const lastIchimoku = lastReal.ichimoku;
  const points = [];

  if (
    lastIchimoku &&
    typeof lastIchimoku.spanA === "number" &&
    typeof lastIchimoku.spanB === "number"
  ) {
    points.push({
      x: lastRealX,
      ySpanA: yScale(lastIchimoku.spanA),
      ySpanB: yScale(lastIchimoku.spanB),
      spanA: lastIchimoku.spanA,
      spanB: lastIchimoku.spanB,
    });
  }

  for (let k = 0; k < projection.length; k++) {
    const p = projection[k];
    if (!p || typeof p.spanA !== "number" || typeof p.spanB !== "number") {
      points.push(null);
      continue;
    }
    points.push({
      x: lastRealX + (k + 1) * barWidth,
      ySpanA: yScale(p.spanA),
      ySpanB: yScale(p.spanB),
      spanA: p.spanA,
      spanB: p.spanB,
    });
  }

  return points;
}

function drawCloud(ctx, points, bullishFill, bearishFill, opacity) {
  const areaGen = d3Area()
    .x((d) => d.x)
    .y0((d) => d.ySpanB)
    .y1((d) => d.ySpanA)
    .context(ctx);

  let currentSegment = [];
  let currentType = null;

  const flush = () => {
    if (currentSegment.length < 2) return;
    const fill = currentType === "bullish" ? bullishFill : bearishFill;
    ctx.fillStyle = hexToRGBA(fill, opacity);
    ctx.beginPath();
    areaGen(currentSegment);
    ctx.fill();
  };

  for (let i = 0; i < points.length; i++) {
    const pt = points[i];
    if (!pt) {
      flush();
      currentSegment = [];
      currentType = null;
      continue;
    }
    const segType = pt.spanA > pt.spanB ? "bullish" : "bearish";
    if (currentType === null) {
      currentType = segType;
      currentSegment.push(pt);
    } else if (currentType === segType) {
      currentSegment.push(pt);
    } else {
      currentSegment.push(pt);
      flush();
      currentSegment = [pt];
      currentType = segType;
    }
  }
  flush();
}

function drawSpanLine(
  ctx,
  points,
  accessor,
  color,
  opacity,
  strokeWidth,
  dashed,
) {
  ctx.save();
  ctx.strokeStyle = hexToRGBA(color, opacity);
  ctx.lineWidth = strokeWidth;
  if (dashed) ctx.setLineDash([4, 4]);
  ctx.beginPath();

  let drawing = false;
  for (let i = 0; i < points.length; i++) {
    const pt = points[i];
    if (!pt) {
      drawing = false;
      continue;
    }
    const y = accessor(pt);
    if (!drawing) {
      ctx.moveTo(pt.x, y);
      drawing = true;
    } else {
      ctx.lineTo(pt.x, y);
    }
  }
  ctx.stroke();
  ctx.restore();
}

function IchimokuFutureCloudOverlay({
  indicatorConfig,
  periodicity,
  bullishFill = "#4CAF50",
  bearishFill = "#F44336",
  cloudOpacity = 0.2,
  strokeWidth = 1,
  dashed = false,
  spanAStrokeOpacity = 1,
  spanBStrokeOpacity = 1,
}) {
  const configKey = JSON.stringify(indicatorConfig);

  const calculator = useMemo(
    () => ichimokuFactory().options(indicatorConfig),
    [configKey],
  );

  const cachedStateRef = useRef(null);

  const drawOnCanvas = useCallback(
    (ctx, moreProps) => {
      const { xScale, chartConfig, plotData, fullData } = moreProps;

      if (!fullData || fullData.length === 0) return;
      if (!plotData || plotData.length < 2) return;
      if (!chartConfig || !chartConfig.yScale) return;

      const projection = calculator.computeFutureProjection(fullData);
      if (!projection || projection.length === 0) return;

      const lastVisible = last(plotData);
      const penultimateVisible = plotData[plotData.length - 2];
      if (
        !lastVisible ||
        !lastVisible.idx ||
        !penultimateVisible ||
        !penultimateVisible.idx
      )
        return;

      const barWidth =
        xScale(lastVisible.idx.index) - xScale(penultimateVisible.idx.index);
      if (!isDefined(barWidth) || barWidth <= 0) return;

      const { yScale } = chartConfig;
      const lastReal = last(fullData);
      const lastRealX = lastReal?.idx ? xScale(lastReal.idx.index) : null;
      const points = buildPoints(
        projection,
        fullData,
        xScale,
        yScale,
        barWidth,
      );
      if (points.length < 2) return;

      cachedStateRef.current = {
        lastRealX,
        barWidth,
        projection,
        lastBar: lastReal,
      };

      ctx.save();
      drawCloud(ctx, points, bullishFill, bearishFill, cloudOpacity);
      drawSpanLine(
        ctx,
        points,
        (d) => d.ySpanA,
        bullishFill,
        spanAStrokeOpacity,
        strokeWidth,
        dashed,
      );
      drawSpanLine(
        ctx,
        points,
        (d) => d.ySpanB,
        bearishFill,
        spanBStrokeOpacity,
        strokeWidth,
        dashed,
      );
      ctx.restore();
    },
    [
      calculator,
      bullishFill,
      bearishFill,
      cloudOpacity,
      strokeWidth,
      dashed,
      spanAStrokeOpacity,
      spanBStrokeOpacity,
    ],
  );

  const drawCursorLabels = useCallback(
    (ctx, moreProps) => {
      const { mouseXY, show, height, width } = moreProps;
      if (!show || !mouseXY || !cachedStateRef.current) return;
      if (!height || !width) return;

      const { lastRealX, barWidth, projection, lastBar } =
        cachedStateRef.current;
      if (!lastRealX || !barWidth || barWidth <= 0) return;

      const mouseX = mouseXY[0];
      // Only activate past the last real bar position
      if (mouseX <= lastRealX) return;

      const k = Math.min(
        Math.max(0, Math.round((mouseX - lastRealX) / barWidth) - 1),
        projection.length - 1,
      );
      const futureX = lastRealX + (k + 1) * barWidth;

      // Erase CrossHairCursor's stuck vertical line at lastRealX (mouse canvas only —
      // the chart canvas with actual candles is untouched)
      ctx.clearRect(Math.round(lastRealX) - 2, 0, 5, height);

      // Draw full crosshair at the correct future position
      ctx.save();
      ctx.strokeStyle = "rgba(0,0,0,0.3)";
      ctx.lineWidth = 3;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(0, mouseXY[1]);
      ctx.lineTo(width, mouseXY[1]);
      ctx.moveTo(futureX, 0);
      ctx.lineTo(futureX, height);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();

      if (!periodicity || !lastBar?.date) return;
      const futureDate = new Date(
        lastBar.date.getTime() + (k + 1) * parsePeriodicityMs(periodicity),
      );
      const label = formatFutureDate(futureDate, periodicity);

      // height = moreProps.height = total content height (all panels). This matches
      // the coordinate space used by MouseCoordinateX in the last panel, so the label
      // appears at the same bottom position regardless of how many panels exist.
      drawEdgeOnCanvas(ctx, {
        coordinate: label,
        show: true,
        type: "vertical",
        orient: "bottom",
        edgeAt: height,
        x1: futureX,
        x2: futureX,
        y1: 0,
        y2: height,
        fill: "#525252",
        opacity: 1,
        fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        fontSize: 13,
        textFill: "#FFFFFF",
        arrowWidth: 0,
        rectHeight: 20,
        hideLine: true,
        strokeOpacity: 1,
        strokeWidth: 1,
      });
    },
    [periodicity],
  );

  return (
    <>
      <GenericChartComponent
        canvasDraw={drawOnCanvas}
        canvasToDraw={getAxisCanvas}
        clip
        drawOn={["pan"]}
      />
      <GenericChartComponent
        canvasDraw={drawCursorLabels}
        canvasToDraw={getMouseCanvas}
        clip={false}
        drawOn={["mousemove", "pan", "drag"]}
      />
    </>
  );
}

IchimokuFutureCloudOverlay.propTypes = {
  indicatorConfig: PropTypes.shape({
    conversionPeriod: PropTypes.number.isRequired,
    basePeriod: PropTypes.number.isRequired,
    spanBPeriod: PropTypes.number.isRequired,
    displacement: PropTypes.number.isRequired,
  }).isRequired,
  periodicity: PropTypes.string,
  bullishFill: PropTypes.string,
  bearishFill: PropTypes.string,
  cloudOpacity: PropTypes.number,
  strokeWidth: PropTypes.number,
  dashed: PropTypes.bool,
  spanAStrokeOpacity: PropTypes.number,
  spanBStrokeOpacity: PropTypes.number,
};

export default React.memo(IchimokuFutureCloudOverlay);
